import { useCallback, useState } from 'react';
import type { z } from 'zod';
import { ApiError, errorMessage } from '@/api/client';

type Fields = Record<string, string>;

/** Validates with a shared zod schema and returns per-field messages. */
export function validate<T extends z.ZodType>(schema: T, values: unknown): { data?: z.output<T>; fields: Fields } {
  const result = schema.safeParse(values);
  if (result.success) return { data: result.data, fields: {} };
  const fields: Fields = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_';
    fields[key] ??= issue.message;
  }
  return { fields };
}

/**
 * Tracks submission state for a form: loading flag, a general error and
 * field-level errors (from client validation or the API's `fields`).
 */
export function useSubmit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Fields>({});

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setLoading(true);
    setError(null);
    setFields({});
    try {
      return await fn();
    } catch (err) {
      if (err instanceof ApiError && err.fields && Object.keys(err.fields).length) setFields(err.fields);
      else setError(errorMessage(err));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, fields, setFields, setError, run };
}
